// Ported from nostro-operations-detail-dialog.component.{ts,html}
// Handles confirm and reject actions for a single nostro operation record.
import { useMemo, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import type {
  ConfirmNostroOperationRequest,
  ExternalServicesOperationsRecord,
  PaymentIntermediariesRecord,
} from '@/types/nostro';
import { NostroOperationsOrderTypeEnum } from '@/types/nostro';
import {
  useConfirmNostroOperationMutation,
  useCancelNostroOperationMutation,
} from './api';

// Mirror Angular's getEnumKeyByValue utility
function enumKeyByValue<T extends Record<string, string>>(
  enumObj: T,
  value: string,
): string {
  const found = (Object.keys(enumObj) as Array<keyof T>).find(
    (k) => enumObj[k] === value,
  );
  return found != null ? String(found) : value;
}

function formatXaf(amount: number | undefined): string {
  if (amount == null) return '';
  return `XAF ${amount.toLocaleString('en', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export interface NostroOperationDetailDialogProps {
  record: ExternalServicesOperationsRecord;
  paymentIntermediaries: PaymentIntermediariesRecord[];
  actionType: 'confirm' | 'reject';
  onClose: (result: ExternalServicesOperationsRecord | null) => void;
}

type FormValues = {
  serviceReference: string;
  externalReference: string;
  notes: string;
};

export function NostroOperationDetailDialog({
  record,
  paymentIntermediaries,
  actionType,
  onClose,
}: NostroOperationDetailDialogProps) {
  const { t } = useTranslation();
  const confirmMutation = useConfirmNostroOperationMutation();
  const cancelMutation = useCancelNostroOperationMutation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<File[]>([]);

  const idPaymentIntermediary =
    record.fields['Payment Intermediary']?.length
      ? record.fields['Payment Intermediary'][0]
      : null;

  const currentBankPayment = useMemo(
    () =>
      paymentIntermediaries.find((p) => String(p.id) === idPaymentIntermediary) ?? null,
    [paymentIntermediaries, idPaymentIntermediary],
  );

  const accountName = record.fields['Provider Account Name']
    ? `${record.fields['Provider Account Name']} ${record.fields['Provider Account Type'] ?? ''}`.trim()
    : '';

  const schema = useMemo(
    () =>
      z
        .object({
          serviceReference: z.string().min(1, t('messages.error-fields', { defaultValue: 'Field is required' })),
          externalReference: z.string().min(1, t('messages.error-fields', { defaultValue: 'Field is required' })),
          notes: z.string(),
        })
        .superRefine((data, ctx) => {
          if (actionType === 'reject' && !data.notes.trim()) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('messages.error-fields', { defaultValue: 'Field is required' }),
              path: ['notes'],
            });
          }
        }),
    [actionType, t],
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      serviceReference: record.fields['External Payment Reference'] ?? '',
      externalReference: '',
      notes: record.fields['Notes'] ?? '',
    },
  });

  const buildRequest = (values: FormValues): ConfirmNostroOperationRequest => ({
    recordId: record.id,
    uuid: record.fields.UUID ?? '',
    idPaymentIntermediary: idPaymentIntermediary ?? '',
    amountPaid: record.fields['Amount to Pay'],
    serviceReference: values.serviceReference,
    externalReference: values.externalReference,
    notes: values.notes,
    operationType: enumKeyByValue(
      NostroOperationsOrderTypeEnum,
      record.fields['Order Type'] ?? '',
    ),
  });

  const onSubmit = async (values: FormValues) => {
    if (!idPaymentIntermediary) {
      void Swal.fire(
        t('messages.error', { defaultValue: 'Error' }),
        t('messages.error-fields', { defaultValue: 'Field is required' }),
        'error',
      );
      return;
    }

    const request = buildRequest(values);

    if (actionType === 'confirm') {
      if (!files.length) {
        void Swal.fire(
          t('messages.error', { defaultValue: 'Error' }),
          t('pages.nostroOperations.dialog.attachmentsRequired', {
            defaultValue: 'Please upload the attachment files',
          }),
          'error',
        );
        return;
      }

      const formData = new FormData();
      files.forEach((file) => formData.append('file[]', file));
      formData.append('confirmNostroOperationRequest', JSON.stringify(request));

      const res = await confirmMutation.mutateAsync(formData);
      if (res.code === 0) {
        onClose(res.result.records[0] ?? null);
      } else {
        void Swal.fire(t('messages.error', { defaultValue: 'Error' }), res.message, 'error');
      }
    } else {
      const res = await cancelMutation.mutateAsync(request);
      if (res.code === 0) {
        onClose(res.result.records[0] ?? null);
      } else {
        void Swal.fire(t('messages.error', { defaultValue: 'Error' }), res.message, 'error');
      }
    }
  };

  const isPending = confirmMutation.isPending || cancelMutation.isPending;

  return (
    <Dialog open onClose={() => onClose(null)} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6">
          {actionType} {record.fields['Order Type']}
        </Typography>
        <Typography variant="subtitle2" color="text.secondary">
          {t('pages.nostroOperations.dialog.reference', { defaultValue: 'Reference' })}:{' '}
          <strong>{record.fields.UUID}</strong>
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          {/* Read-only details */}
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              {t('pages.nostroOperations.dialog.amount', { defaultValue: 'Amount' })}
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {formatXaf(record.fields['Amount to Pay'])}
            </Typography>
          </Stack>

          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              {t('pages.nostroOperations.dialog.status', { defaultValue: 'Status' })}
            </Typography>
            <Typography variant="body2">{record.fields.Status}</Typography>
          </Stack>

          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              {t('pages.nostroOperations.dialog.accountName', { defaultValue: 'Account name' })}
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {accountName}
            </Typography>
          </Stack>

          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              {t('pages.nostroOperations.dialog.accountNumber', { defaultValue: 'Account number' })}
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {record.fields['Provider Account Code']}
            </Typography>
          </Stack>

          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              {t('pages.nostroOperations.dialog.paymentIntermediary', {
                defaultValue: 'Payment intermediary',
              })}
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {currentBankPayment?.name ?? '—'}
            </Typography>
          </Stack>

          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              {t('pages.nostroOperations.dialog.paymentAccountNumber', {
                defaultValue: 'Payment account number',
              })}
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {currentBankPayment?.accountNumber ?? '—'}
            </Typography>
          </Stack>

          <Divider />

          {/* Editable fields */}
          <TextField
            label={t('pages.nostroOperations.dialog.serviceReference', {
              defaultValue: 'Service reference',
            })}
            size="small"
            fullWidth
            required
            {...register('serviceReference')}
            error={!!errors.serviceReference}
            helperText={errors.serviceReference?.message}
            placeholder={t('pages.nostroOperations.dialog.externalServiceReferencePlaceHolder', {
              defaultValue: 'Enter service reference',
            })}
          />

          <TextField
            label={t('pages.nostroOperations.dialog.paymentReference', {
              defaultValue: 'Payment reference',
            })}
            size="small"
            fullWidth
            required
            {...register('externalReference')}
            error={!!errors.externalReference}
            helperText={errors.externalReference?.message}
            placeholder={t('pages.nostroOperations.dialog.externalPaymentReferencePlaceHolder', {
              defaultValue: 'Enter payment reference',
            })}
          />

          {actionType === 'confirm' && (
            <Stack spacing={0.5}>
              <Typography variant="body2" color="text.secondary">
                {t('pages.nostroOperations.dialog.attachments', { defaultValue: 'Attachments' })}{' '}
                *
              </Typography>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/jpg,image/jpeg,image/bmp,image/gif,image/png,image/svg+xml,application/pdf"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const selected = Array.from(e.target.files ?? []);
                  setFiles(selected);
                }}
              />
              <Button
                variant="outlined"
                size="small"
                onClick={() => fileInputRef.current?.click()}
              >
                {t('pages.nostroOperations.dialog.dragAndDrop1', {
                  defaultValue: 'Choose files',
                })}
              </Button>
              {files.length > 0 && (
                <Typography variant="caption">
                  {files.map((f) => f.name).join(', ')}
                </Typography>
              )}
            </Stack>
          )}

          {actionType === 'reject' && (
            <TextField
              label={t('pages.nostroOperations.dialog.notes', { defaultValue: 'Notes' })}
              size="small"
              fullWidth
              required
              multiline
              rows={3}
              {...register('notes')}
              error={!!errors.notes}
              helperText={errors.notes?.message}
            />
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={() => onClose(null)} disabled={isPending}>
          {t('pages.nostroOperations.dialog.cancel', { defaultValue: 'Cancel' })}
        </Button>
        {actionType === 'confirm' && (
          <Button
            variant="contained"
            color="success"
            onClick={() => void handleSubmit(onSubmit)()}
            disabled={isPending}
          >
            {t('pages.nostroOperations.dialog.validate', { defaultValue: 'Validate' })}
          </Button>
        )}
        {actionType === 'reject' && (
          <Button
            variant="contained"
            color="error"
            onClick={() => void handleSubmit(onSubmit)()}
            disabled={isPending}
          >
            {t('pages.nostroOperations.dialog.reject', { defaultValue: 'Reject' })}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
